'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import TranslatorPanel from './components/TranslatorPanel';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Participant {
  id: string;
  name: string;
  initials: string;
  role: 'consultant' | 'client';
  isMuted: boolean;
  isCameraOff: boolean;
  isScreenSharing: boolean;
  isSpeaking: boolean;
}

interface ChatMessage {
  id: string;
  sender: string;
  senderInitials: string;
  text: string;
  timestamp: string;
  isMe: boolean;
}

type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped';
type ConnectionStatus = 'connecting' | 'connected' | 'poor';
type SidePanelTab = 'chat' | 'participants' | 'translator' | null;

// ─── Mock Booking Data ─────────────────────────────────────────────────────────
const bookingData = {
  bookingId: 'BK-2026-0847',
  title: 'Islamic Finance Consultation',
  consultant: {
    id: 'c1',
    name: 'Yusuf Al-Rashid',
    initials: 'YR',
    role: 'consultant' as const,
    title: 'Islamic Finance & Sukuk Expert',
    category: 'Islamic Finance',
  },
  client: {
    id: 'cl1',
    name: 'Ahmed Hassan',
    initials: 'AH',
    role: 'client' as const,
  },
  scheduledDuration: 60, // minutes
  scheduledAt: '2026-07-08 · 10:00 AM',
  topic: 'Sukuk structuring for SME expansion',
};

const initialMessages: ChatMessage[] = [
  {
    id: 'm1',
    sender: 'Yusuf Al-Rashid',
    senderInitials: 'YR',
    text: 'Assalamu Alaikum! I can see you joined. Ready to begin?',
    timestamp: '10:00 AM',
    isMe: false,
  },
  {
    id: 'm2',
    sender: 'Ahmed Hassan',
    senderInitials: 'AH',
    text: 'Wa Alaikum Assalam! Yes, all set.',
    timestamp: '10:01 AM',
    isMe: true,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDuration(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatRecordingSize(secs: number): string {
  const mb = (secs * 0.5).toFixed(1);
  return `${mb} MB`;
}

// ─── Component ─────────────────────────────────────────────────────────────────
export default function VideoSessionPage() {
  // Media controls
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  // UI state
  const [sidePanel, setSidePanel] = useState<SidePanelTab>(null);
  const [showEndModal, setShowEndModal] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pinnedId, setPinnedId] = useState<string>('c1');

  // Timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');

  // Recording
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [showRecordingConsent, setShowRecordingConsent] = useState(false);
  const [recordingConsented, setRecordingConsented] = useState(false);
  const [savedRecordings, setSavedRecordings] = useState<{ id: string; duration: string; size: string; startedAt: string }[]>([]);

  // Chat
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [unreadCount, setUnreadCount] = useState(0);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const participants: Participant[] = [
    {
      id: 'c1',
      name: bookingData.consultant.name,
      initials: bookingData.consultant.initials,
      role: 'consultant',
      isMuted: false,
      isCameraOff: false,
      isScreenSharing,
      isSpeaking: true,
    },
    {
      id: 'cl1',
      name: bookingData.client.name,
      initials: bookingData.client.initials,
      role: 'client',
      isMuted,
      isCameraOff,
      isScreenSharing: false,
      isSpeaking: false,
    },
  ];

  // Simulate connection
  useEffect(() => {
    const t = setTimeout(() => setConnectionStatus('connected'), 2000);
    return () => clearTimeout(t);
  }, []);

  // Session timer
  useEffect(() => {
    if (sessionEnded || connectionStatus !== 'connected') return;
    const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [sessionEnded, connectionStatus]);

  // Recording timer
  useEffect(() => {
    if (recordingState !== 'recording') return;
    const interval = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [recordingState]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Track unread messages when chat is closed
  useEffect(() => {
    if (sidePanel !== 'chat') {
      setUnreadCount((c) => c + 0); // reset handled on open
    }
  }, [sidePanel]);

  const openChat = () => {
    setSidePanel('chat');
    setUnreadCount(0);
  };

  const handleSendMessage = useCallback(() => {
    if (!chatInput.trim()) return;
    const now = new Date();
    const newMsg: ChatMessage = {
      id: `m${Date.now()}`,
      sender: bookingData.client.name,
      senderInitials: bookingData.client.initials,
      text: chatInput.trim(),
      timestamp: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isMe: true,
    };
    setMessages((prev) => [...prev, newMsg]);
    setChatInput('');

    // Simulate consultant reply after 3s
    setTimeout(() => {
      const reply: ChatMessage = {
        id: `m${Date.now() + 1}`,
        sender: bookingData.consultant.name,
        senderInitials: bookingData.consultant.initials,
        text: 'Understood. Let me elaborate on that point.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isMe: false,
      };
      setMessages((prev) => [...prev, reply]);
      if (sidePanel !== 'chat') setUnreadCount((c) => c + 1);
    }, 3000);
  }, [chatInput, sidePanel]);

  const handleStartRecording = () => {
    if (!recordingConsented) {
      setShowRecordingConsent(true);
      return;
    }
    setRecordingState('recording');
    setRecordingSeconds(0);
  };

  const handleConsentAndRecord = () => {
    setRecordingConsented(true);
    setShowRecordingConsent(false);
    setRecordingState('recording');
    setRecordingSeconds(0);
  };

  const handlePauseRecording = () => {
    setRecordingState('paused');
  };

  const handleResumeRecording = () => {
    setRecordingState('recording');
  };

  const handleStopRecording = () => {
    const rec = {
      id: `rec-${Date.now()}`,
      duration: formatDuration(recordingSeconds),
      size: formatRecordingSize(recordingSeconds),
      startedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setSavedRecordings((prev) => [...prev, rec]);
    setRecordingState('stopped');
    setRecordingSeconds(0);
    setTimeout(() => setRecordingState('idle'), 2000);
  };

  const handleEndSession = () => {
    if (recordingState === 'recording' || recordingState === 'paused') {
      handleStopRecording();
    }
    setSessionEnded(true);
    setShowEndModal(false);
  };

  const scheduledSecs = bookingData.scheduledDuration * 60;
  const timeRemaining = scheduledSecs - elapsedSeconds;
  const isNearEnd = timeRemaining <= 300 && timeRemaining > 0; // last 5 min
  const isOvertime = timeRemaining <= 0;

  const pinnedParticipant = participants.find((p) => p.id === pinnedId) ?? participants[0];
  const thumbnailParticipants = participants.filter((p) => p.id !== pinnedParticipant?.id);

  // ─── Session Ended Screen ───────────────────────────────────────────────────
  if (sessionEnded) {
    return (
      <div className="min-h-screen bg-[#0a0c12] flex items-center justify-center p-6">
        <div className="bg-[#13161f] border border-white/10 rounded-2xl p-10 max-w-lg w-full shadow-2xl">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-5">
            <Icon name="CheckCircleIcon" size={36} className="text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white text-center mb-1">Session Complete</h2>
          <p className="text-white/40 text-sm text-center mb-6">{bookingData.title}</p>

          <div className="bg-white/5 rounded-xl p-5 mb-5 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-white/50">Booking ID</span>
              <span className="text-white font-mono font-medium">{bookingData.bookingId}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/50">Consultant</span>
              <span className="text-white font-medium">{bookingData.consultant.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/50">Duration</span>
              <span className="text-white font-medium">{formatDuration(elapsedSeconds)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/50">Status</span>
              <span className="text-emerald-400 font-medium">Completed</span>
            </div>
          </div>

          {savedRecordings.length > 0 && (
            <div className="bg-white/5 rounded-xl p-4 mb-5">
              <p className="text-white/60 text-xs font-medium uppercase tracking-wider mb-3">Recordings Saved</p>
              <div className="space-y-2">
                {savedRecordings.map((rec) => (
                  <div key={rec.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="FilmIcon" size={14} className="text-primary" />
                      <span className="text-white text-sm">{rec.duration}</span>
                      <span className="text-white/30 text-xs">· {rec.size}</span>
                    </div>
                    <span className="text-white/30 text-xs">Started {rec.startedAt}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Link
              href="/my-bookings"
              className="flex-1 py-2.5 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/15 transition-colors text-center"
            >
              My Bookings
            </Link>
            <Link
              href="/session-reviews"
              className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors text-center"
            >
              Leave a Review
            </Link>
          </div>
          {savedRecordings.length > 0 && (
            <Link
              href="/consultation-recordings"
              className="block mt-3 py-2.5 rounded-xl bg-white/5 text-white/60 text-sm font-medium hover:bg-white/10 transition-colors text-center"
            >
              View All Recordings
            </Link>
          )}
        </div>
      </div>
    );
  }

  // ─── Connecting Screen ──────────────────────────────────────────────────────
  if (connectionStatus === 'connecting') {
    return (
      <div className="min-h-screen bg-[#0a0c12] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-2 border-primary/20 animate-ping" />
            <div className="absolute inset-3 rounded-full border-2 border-primary/40 animate-ping" style={{ animationDelay: '0.3s' }} />
            <div className="w-24 h-24 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Icon name="VideoCameraIcon" size={36} className="text-primary" />
            </div>
          </div>
          <h2 className="text-white text-xl font-bold mb-2">Joining Session…</h2>
          <p className="text-white/50 text-sm mb-1">{bookingData.title}</p>
          <p className="text-white/30 text-xs mb-6">Booking {bookingData.bookingId}</p>
          <div className="bg-white/5 rounded-xl p-4 text-left space-y-2">
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Icon name="UserCircleIcon" size={14} className="text-primary" />
              <span>Connecting with {bookingData.consultant.name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Icon name="CalendarIcon" size={14} className="text-primary" />
              <span>{bookingData.scheduledAt}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Icon name="ClockIcon" size={14} className="text-primary" />
              <span>{bookingData.scheduledDuration} min session</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main Session Room ──────────────────────────────────────────────────────
  return (
    <div className={`flex flex-col bg-[#0a0c12] ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}`}>

      {/* ── Top Bar ── */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#0a0c12]/95 backdrop-blur border-b border-white/5 z-10 flex-shrink-0">
        {/* Left: status + session info */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className={`w-2 h-2 rounded-full animate-pulse ${connectionStatus === 'connected' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
            <span className="text-white/40 text-xs hidden sm:block">
              {connectionStatus === 'connected' ? 'Live' : 'Poor'}
            </span>
          </div>
          <div className="w-px h-4 bg-white/10 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold leading-tight truncate">{bookingData.title}</p>
            <p className="text-white/30 text-xs truncate">{bookingData.bookingId} · {bookingData.consultant.title}</p>
          </div>
        </div>

        {/* Center: timer */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 border transition-colors ${
            isOvertime
              ? 'bg-red-500/10 border-red-500/30'
              : isNearEnd
              ? 'bg-amber-500/10 border-amber-500/30' :'bg-white/5 border-white/5'
          }`}>
            <Icon
              name="ClockIcon"
              size={13}
              className={isOvertime ? 'text-red-400' : isNearEnd ? 'text-amber-400' : 'text-white/40'}
            />
            <span className={`font-mono text-sm font-medium ${isOvertime ? 'text-red-400' : isNearEnd ? 'text-amber-400' : 'text-white'}`}>
              {formatDuration(elapsedSeconds)}
            </span>
            <span className={`text-xs ${isOvertime ? 'text-red-400/60' : 'text-white/25'}`}>
              {isOvertime ? 'overtime' : `/ ${bookingData.scheduledDuration}m`}
            </span>
          </div>

          {/* Recording badge */}
          {recordingState === 'recording' && (
            <div className="flex items-center gap-1.5 bg-red-500/15 border border-red-500/30 rounded-lg px-2.5 py-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-red-400 text-xs font-medium font-mono">{formatDuration(recordingSeconds)}</span>
            </div>
          )}
          {recordingState === 'paused' && (
            <div className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5">
              <Icon name="PauseIcon" size={12} className="text-amber-400" />
              <span className="text-amber-400 text-xs font-medium">Paused</span>
            </div>
          )}
        </div>

        {/* Right: panel toggles + fullscreen */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setSidePanel(sidePanel === 'participants' ? null : 'participants')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${sidePanel === 'participants' ? 'bg-primary/20 text-primary' : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'}`}
          >
            <Icon name="UsersIcon" size={13} />
            <span className="hidden sm:block">{participants.length}</span>
          </button>

          <button
            onClick={() => { openChat(); setSidePanel(sidePanel === 'chat' ? null : 'chat'); }}
            className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${sidePanel === 'chat' ? 'bg-primary/20 text-primary' : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'}`}
          >
            <Icon name="ChatBubbleLeftRightIcon" size={13} />
            <span className="hidden sm:block">Chat</span>
            {unreadCount > 0 && sidePanel !== 'chat' && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {/* Translator toggle */}
          <button
            onClick={() => setSidePanel(sidePanel === 'translator' ? null : 'translator')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${sidePanel === 'translator' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'}`}
            title="Live Translator"
          >
            <Icon name="LanguageIcon" size={13} />
            <span className="hidden sm:block">Translate</span>
          </button>

          <button
            onClick={() => setIsFullscreen((f) => !f)}
            className="p-1.5 rounded-lg bg-white/5 text-white/40 hover:bg-white/10 hover:text-white transition-colors"
          >
            <Icon name={isFullscreen ? 'ArrowsPointingInIcon' : 'ArrowsPointingOutIcon'} size={15} />
          </button>
        </div>
      </div>

      {/* ── Near-end warning banner ── */}
      {isNearEnd && !isOvertime && (
        <div className="flex items-center justify-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 flex-shrink-0">
          <Icon name="ExclamationTriangleIcon" size={14} className="text-amber-400" />
          <span className="text-amber-300 text-xs font-medium">
            {Math.ceil(timeRemaining / 60)} minute{Math.ceil(timeRemaining / 60) !== 1 ? 's' : ''} remaining in scheduled session
          </span>
        </div>
      )}
      {isOvertime && (
        <div className="flex items-center justify-center gap-2 px-4 py-2 bg-red-500/10 border-b border-red-500/20 flex-shrink-0">
          <Icon name="ExclamationCircleIcon" size={14} className="text-red-400" />
          <span className="text-red-300 text-xs font-medium">Session is running over scheduled time</span>
        </div>
      )}

      {/* ── Main Area ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Video Grid ── */}
        <div className="flex-1 flex flex-col p-4 gap-3 overflow-hidden min-w-0">

          {/* Main (pinned) video */}
          <div className="flex-1 relative rounded-2xl overflow-hidden bg-[#13161f] border border-white/5 min-h-0">
            {pinnedParticipant?.isCameraOff ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <div className="w-20 h-20 rounded-full bg-white/8 border border-white/10 flex items-center justify-center text-3xl font-bold text-white">
                  {pinnedParticipant?.initials}
                </div>
                <p className="text-white/40 text-sm">Camera is off</p>
              </div>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#181d2e] via-[#0f1420] to-[#0a0c12] flex items-center justify-center">
                {pinnedParticipant?.isScreenSharing ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <Icon name="ComputerDesktopIcon" size={32} className="text-primary/70" />
                    </div>
                    <p className="text-white/50 text-sm">Screen sharing active</p>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/20 flex items-center justify-center text-4xl font-bold text-white/70">
                    {pinnedParticipant?.initials}
                  </div>
                )}
              </div>
            )}

            {/* Speaking ring */}
            {pinnedParticipant?.isSpeaking && !pinnedParticipant?.isMuted && (
              <div className="absolute inset-0 rounded-2xl border-2 border-emerald-400/50 pointer-events-none" />
            )}

            {/* Name tag */}
            <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5">
              <span className="text-white text-sm font-medium">{pinnedParticipant?.name}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${pinnedParticipant?.role === 'consultant' ? 'bg-primary/25 text-primary' : 'bg-white/10 text-white/50'}`}>
                {pinnedParticipant?.role}
              </span>
              {pinnedParticipant?.isMuted && (
                <Icon name="MicrophoneIcon" size={12} className="text-red-400" />
              )}
            </div>

            {/* Unpin */}
            <button
              onClick={() => setPinnedId(thumbnailParticipants[0]?.id ?? 'c1')}
              className="absolute top-3 right-3 p-1.5 bg-black/40 backdrop-blur-sm rounded-lg text-white/40 hover:text-white transition-colors"
              title="Switch view"
            >
              <Icon name="ArrowsRightLeftIcon" size={15} />
            </button>
          </div>

          {/* Thumbnail strip */}
          {thumbnailParticipants.length > 0 && (
            <div className="flex gap-3 h-28 flex-shrink-0">
              {thumbnailParticipants.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPinnedId(p.id)}
                  className="relative flex-1 rounded-xl overflow-hidden bg-[#13161f] border border-white/5 hover:border-primary/30 transition-colors group"
                >
                  {p.isCameraOff ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-white/8 flex items-center justify-center text-sm font-bold text-white">
                        {p.initials}
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#181d2e] to-[#0a0c12] flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/15 flex items-center justify-center text-sm font-bold text-white/70">
                        {p.initials}
                      </div>
                    </div>
                  )}
                  {p.isSpeaking && !p.isMuted && (
                    <div className="absolute inset-0 rounded-xl border-2 border-emerald-400/50 pointer-events-none" />
                  )}
                  <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                    <span className="text-white text-xs font-medium bg-black/50 rounded px-1.5 py-0.5 truncate">
                      {p.name.split(' ')[0]}
                    </span>
                    {p.isMuted && <Icon name="MicrophoneIcon" size={10} className="text-red-400" />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Side Panel ── */}
        {sidePanel && (
          <div className="w-72 flex flex-col bg-[#0e1018] border-l border-white/5 flex-shrink-0">
            {/* Panel tabs */}
            <div className="flex border-b border-white/5 flex-shrink-0">
              <button
                onClick={() => setSidePanel('chat')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-colors relative ${sidePanel === 'chat' ? 'text-primary' : 'text-white/40 hover:text-white/70'}`}
              >
                <Icon name="ChatBubbleLeftRightIcon" size={13} />
                Chat
                {sidePanel === 'chat' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
              </button>
              <button
                onClick={() => setSidePanel('participants')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-colors relative ${sidePanel === 'participants' ? 'text-primary' : 'text-white/40 hover:text-white/70'}`}
              >
                <Icon name="UsersIcon" size={13} />
                People
                {sidePanel === 'participants' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
              </button>
              <button
                onClick={() => setSidePanel('translator')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-colors relative ${sidePanel === 'translator' ? 'text-emerald-400' : 'text-white/40 hover:text-white/70'}`}
              >
                <Icon name="LanguageIcon" size={13} />
                <span className="hidden sm:block">Translate</span>
                {sidePanel === 'translator' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-400 rounded-full" />}
              </button>
              <button
                onClick={() => setSidePanel(null)}
                className="px-3 py-3 text-white/30 hover:text-white/60 transition-colors"
              >
                <Icon name="XMarkIcon" size={15} />
              </button>
            </div>

            {/* ── Translator Panel ── */}
            {sidePanel === 'translator' && (
              <div className="flex-1 overflow-hidden min-h-0">
                <TranslatorPanel
                  myName={bookingData.client.name}
                  myInitials={bookingData.client.initials}
                  otherName={bookingData.consultant.name}
                  otherInitials={bookingData.consultant.initials}
                  onClose={() => setSidePanel(null)}
                />
              </div>
            )}

            {/* ── Chat ── */}
            {sidePanel === 'chat' && (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`flex flex-col ${msg.isMe ? 'items-end' : 'items-start'}`}>
                      {!msg.isMe && (
                        <div className="flex items-center gap-1.5 mb-1 px-1">
                          <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                            {msg.senderInitials}
                          </div>
                          <span className="text-white/40 text-xs">{msg.sender.split(' ')[0]}</span>
                        </div>
                      )}
                      <div className={`max-w-[88%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                        msg.isMe
                          ? 'bg-primary/20 text-white rounded-br-sm' :'bg-white/6 text-white/85 rounded-bl-sm'
                      }`}>
                        {msg.text}
                      </div>
                      <span className="text-white/20 text-[10px] mt-1 px-1">{msg.timestamp}</span>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-3 border-t border-white/5 flex-shrink-0">
                  <div className="flex items-center gap-2 bg-white/5 border border-white/8 rounded-xl px-3 py-2">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder="Message…"
                      className="flex-1 bg-transparent text-white text-sm placeholder-white/25 outline-none"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!chatInput.trim()}
                      className="p-1.5 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 disabled:opacity-30 transition-colors flex-shrink-0"
                    >
                      <Icon name="PaperAirplaneIcon" size={13} />
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── Participants ── */}
            {sidePanel === 'participants' && (
              <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                <p className="text-white/30 text-xs uppercase tracking-wider font-medium px-1 mb-3">
                  {participants.length} in session
                </p>
                {participants.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/4 border border-white/5">
                    <div className="relative flex-shrink-0">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 border border-primary/15 flex items-center justify-center text-sm font-bold text-white">
                        {p.initials}
                      </div>
                      {p.isSpeaking && !p.isMuted && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0e1018]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{p.name}</p>
                      <p className="text-white/35 text-xs capitalize">{p.role}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Icon
                        name="MicrophoneIcon"
                        size={13}
                        className={p.isMuted ? 'text-red-400' : 'text-emerald-400'}
                      />
                      <Icon
                        name={p.isCameraOff ? 'VideoCameraSlashIcon' : 'VideoCameraIcon'}
                        size={13}
                        className={p.isCameraOff ? 'text-red-400' : 'text-emerald-400'}
                      />
                    </div>
                  </div>
                ))}

                {/* Session info */}
                <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                  <p className="text-white/30 text-xs uppercase tracking-wider font-medium px-1">Session Info</p>
                  <div className="bg-white/4 rounded-xl p-3 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Booking ID</span>
                      <span className="text-white font-mono">{bookingData.bookingId}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Topic</span>
                      <span className="text-white/70 text-right max-w-[60%] truncate">{bookingData.topic}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Scheduled</span>
                      <span className="text-white/70">{bookingData.scheduledDuration} min</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Elapsed</span>
                      <span className={`font-mono font-medium ${isOvertime ? 'text-red-400' : 'text-white'}`}>
                        {formatDuration(elapsedSeconds)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Recordings list */}
                {savedRecordings.length > 0 && (
                  <div className="pt-2 space-y-2">
                    <p className="text-white/30 text-xs uppercase tracking-wider font-medium px-1">Saved Recordings</p>
                    {savedRecordings.map((rec) => (
                      <div key={rec.id} className="bg-white/4 rounded-xl p-3 flex items-center gap-2">
                        <Icon name="FilmIcon" size={14} className="text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-medium">{rec.duration}</p>
                          <p className="text-white/30 text-[10px]">{rec.size} · {rec.startedAt}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Controls Bar ── */}
      <div className="flex items-center justify-center gap-2 sm:gap-3 px-4 py-4 bg-[#0a0c12]/95 backdrop-blur border-t border-white/5 flex-shrink-0 flex-wrap">

        {/* Mute */}
        <button
          onClick={() => setIsMuted((m) => !m)}
          className="flex flex-col items-center gap-1.5 group"
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${isMuted ? 'bg-red-500/20 border border-red-500/40 text-red-400' : 'bg-white/8 border border-white/8 text-white hover:bg-white/14'}`}>
            <Icon name="MicrophoneIcon" size={20} />
          </div>
          <span className="text-white/35 text-xs group-hover:text-white/60 transition-colors">
            {isMuted ? 'Unmute' : 'Mute'}
          </span>
        </button>

        {/* Camera */}
        <button
          onClick={() => setIsCameraOff((c) => !c)}
          className="flex flex-col items-center gap-1.5 group"
          title={isCameraOff ? 'Start Camera' : 'Stop Camera'}
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${isCameraOff ? 'bg-red-500/20 border border-red-500/40 text-red-400' : 'bg-white/8 border border-white/8 text-white hover:bg-white/14'}`}>
            <Icon name={isCameraOff ? 'VideoCameraSlashIcon' : 'VideoCameraIcon'} size={20} />
          </div>
          <span className="text-white/35 text-xs group-hover:text-white/60 transition-colors">
            {isCameraOff ? 'Start Cam' : 'Camera'}
          </span>
        </button>

        {/* Screen Share */}
        <button
          onClick={() => setIsScreenSharing((s) => !s)}
          className="flex flex-col items-center gap-1.5 group"
          title={isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${isScreenSharing ? 'bg-primary/20 border border-primary/40 text-primary' : 'bg-white/8 border border-white/8 text-white hover:bg-white/14'}`}>
            <Icon name="ComputerDesktopIcon" size={20} />
          </div>
          <span className="text-white/35 text-xs group-hover:text-white/60 transition-colors">
            {isScreenSharing ? 'Stop' : 'Share'}
          </span>
        </button>

        {/* Recording */}
        {recordingState === 'idle' || recordingState === 'stopped' ? (
          <button
            onClick={handleStartRecording}
            className="flex flex-col items-center gap-1.5 group"
            title="Start Recording"
          >
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-white/8 border border-white/8 text-white hover:bg-white/14 transition-all duration-200">
              <Icon name="FilmIcon" size={20} />
            </div>
            <span className="text-white/35 text-xs group-hover:text-white/60 transition-colors">Record</span>
          </button>
        ) : recordingState === 'recording' ? (
          <div className="flex flex-col items-center gap-1.5">
            <div className="flex gap-1">
              <button
                onClick={handlePauseRecording}
                className="w-6 h-12 rounded-l-2xl flex items-center justify-center bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-colors"
                title="Pause Recording"
              >
                <Icon name="PauseIcon" size={14} />
              </button>
              <button
                onClick={handleStopRecording}
                className="w-6 h-12 rounded-r-2xl flex items-center justify-center bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-colors"
                title="Stop Recording"
              >
                <Icon name="StopIcon" size={14} />
              </button>
            </div>
            <span className="text-red-400/70 text-xs">Recording</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <div className="flex gap-1">
              <button
                onClick={handleResumeRecording}
                className="w-6 h-12 rounded-l-2xl flex items-center justify-center bg-amber-500/15 border border-amber-500/30 text-amber-400 hover:bg-amber-500/25 transition-colors"
                title="Resume Recording"
              >
                <Icon name="PlayIcon" size={14} />
              </button>
              <button
                onClick={handleStopRecording}
                className="w-6 h-12 rounded-r-2xl flex items-center justify-center bg-amber-500/15 border border-amber-500/30 text-amber-400 hover:bg-amber-500/25 transition-colors"
                title="Stop Recording"
              >
                <Icon name="StopIcon" size={14} />
              </button>
            </div>
            <span className="text-amber-400/70 text-xs">Paused</span>
          </div>
        )}

        {/* Chat */}
        <button
          onClick={() => { setSidePanel(sidePanel === 'chat' ? null : 'chat'); setUnreadCount(0); }}
          className="flex flex-col items-center gap-1.5 group relative"
          title="Chat"
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${sidePanel === 'chat' ? 'bg-primary/20 border border-primary/40 text-primary' : 'bg-white/8 border border-white/8 text-white hover:bg-white/14'}`}>
            <Icon name="ChatBubbleLeftRightIcon" size={20} />
          </div>
          {unreadCount > 0 && sidePanel !== 'chat' && (
            <span className="absolute -top-1 right-0 w-4 h-4 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
              {unreadCount}
            </span>
          )}
          <span className="text-white/35 text-xs group-hover:text-white/60 transition-colors">Chat</span>
        </button>

        {/* Participants */}
        <button
          onClick={() => setSidePanel(sidePanel === 'participants' ? null : 'participants')}
          className="flex flex-col items-center gap-1.5 group"
          title="Participants"
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${sidePanel === 'participants' ? 'bg-primary/20 border border-primary/40 text-primary' : 'bg-white/8 border border-white/8 text-white hover:bg-white/14'}`}>
            <Icon name="UsersIcon" size={20} />
          </div>
          <span className="text-white/35 text-xs group-hover:text-white/60 transition-colors">People</span>
        </button>

        {/* Translator button in controls bar */}
        <button
          onClick={() => setSidePanel(sidePanel === 'translator' ? null : 'translator')}
          className="flex flex-col items-center gap-1.5 group"
          title="Live Translator"
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${sidePanel === 'translator' ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400' : 'bg-white/8 border border-white/8 text-white hover:bg-white/14'}`}>
            <Icon name="LanguageIcon" size={20} />
          </div>
          <span className="text-white/35 text-xs group-hover:text-white/60 transition-colors">Translate</span>
        </button>

        {/* Divider */}
        <div className="w-px h-10 bg-white/8 mx-1" />

        {/* End Session */}
        <button
          onClick={() => setShowEndModal(true)}
          className="flex flex-col items-center gap-1.5 group"
          title="End Session"
        >
          <div className="w-14 h-12 rounded-2xl flex items-center justify-center bg-red-500 hover:bg-red-600 text-white transition-all duration-200 shadow-lg shadow-red-500/20">
            <Icon name="PhoneXMarkIcon" size={22} />
          </div>
          <span className="text-red-400/60 text-xs group-hover:text-red-400 transition-colors">End</span>
        </button>
      </div>

      {/* ── Recording Consent Modal ── */}
      {showRecordingConsent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#13161f] border border-white/10 rounded-2xl p-7 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4">
              <Icon name="FilmIcon" size={24} className="text-primary" />
            </div>
            <h3 className="text-white text-lg font-bold text-center mb-2">Record Session?</h3>
            <p className="text-white/50 text-sm text-center mb-5 leading-relaxed">
              Both participants will be notified that this session is being recorded. Recordings are stored securely and accessible from your Recordings page.
            </p>
            <div className="bg-white/5 rounded-xl p-3 mb-5 space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-white/50">
                <Icon name="LockClosedIcon" size={12} className="text-primary" />
                <span>Encrypted and stored securely</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-white/50">
                <Icon name="UserGroupIcon" size={12} className="text-primary" />
                <span>Both participants notified</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-white/50">
                <Icon name="FolderIcon" size={12} className="text-primary" />
                <span>Available in Consultation Recordings</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRecordingConsent(false)}
                className="flex-1 py-2.5 rounded-xl bg-white/8 text-white text-sm font-medium hover:bg-white/14 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConsentAndRecord}
                className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Start Recording
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── End Session Modal ── */}
      {showEndModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#13161f] border border-white/10 rounded-2xl p-7 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <Icon name="PhoneXMarkIcon" size={24} className="text-red-400" />
            </div>
            <h3 className="text-white text-lg font-bold text-center mb-2">End Session?</h3>
            <p className="text-white/50 text-sm text-center mb-2">
              This will end the session for all participants.
            </p>
            <p className="text-white/30 text-xs text-center mb-5">
              Duration: <span className="text-white font-mono font-medium">{formatDuration(elapsedSeconds)}</span>
              {(recordingState === 'recording' || recordingState === 'paused') && (
                <span className="text-amber-400"> · Recording will be saved</span>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-white/8 text-white text-sm font-medium hover:bg-white/14 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEndSession}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
